import React, { Children } from 'react';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Table from '../../components/table/table';
import { economicActivities } from '../../mocks/EconomicActivities';
import { ivaStatuses } from '../../mocks/IvaStatuses';
import Checkbox from '../../components/checkBox/CheckBox';
import { useClientTheme } from '../../context/ThemeContext';
import { useConfig } from '../../context/ConfigContext';
import { formatDateOnlyLocal } from '../../utils/dateOnly';
import { normalizeDgiStatus } from '../Origination/normalizeUruguayClientData';

export interface GeneralDataTabClientData {
  id: number;
  cuit: string;
  verified: boolean;
  has_valid_corporate_composition: boolean;
  category_type?: {
    id: number;
    name: string;
  };
  basic_data: {
    company_name: string;
    company_address: string;
    state: string;
    city: string;
    phone_number: string;
    email: string;
    legal_entity: string;
    activity: string;
    iva_status: string;
    date_incorporation: string;
    economic_group: string | null;
    postal_code?: string | null;
    contractual_link?: boolean | null;
    politically_exposed?: boolean | null;
    state_supplier_registry?: boolean | null;
    dgi_status?: string | null;
    dgi_issue_date?: string | null;
    dgi_expiration_date?: string | null;
    bps_certificate_number?: string | null;
    bps_issue_date?: string | null;
    bps_expiration_date?: string | null;
  };
  corporate_structure: Array<{
    ci: string;
    first_name: string;
    last_name: string;
    ownership_interest: string;
    position: string;
    assets: string;
    value: number;
  }>;
}

interface GeneralDataTabProps {
  clientData: GeneralDataTabClientData;
}

const formatSummaryDate = (dateStr: string | null | undefined): string => {
  if (!dateStr?.trim()) return '-';
  const trimmed = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split('-');
    return `${day}-${month}-${year}`;
  }
  return trimmed;
};

const formatBooleanAsYesNo = (value: boolean | null | undefined) =>
  value === true ? 'Sí' : value === false ? 'No' : '-';

const GRID_DATOS_GENERALES: React.CSSProperties = {
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) minmax(0, 0.55fr)',
};

const GRID_THREE_COL: React.CSSProperties = {
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
};

const ROW_GAP = '16px';

const padToGridColumns = (cells: React.ReactNode[], columnCount: number) => {
  const padded = [...cells];
  while (padded.length < columnCount) {
    padded.push(<span key={`grid-empty-${padded.length}`} aria-hidden='true' />);
  }
  return padded;
};

interface FieldRowProps {
  children: React.ReactNode;
  variant?: 'datosGenerales' | 'threeCol';
}

const FieldRow: React.FC<FieldRowProps> = ({ children, variant = 'datosGenerales' }) => {
  const columnCount = variant === 'datosGenerales' ? 4 : 3;
  return (
    <Card
      display='grid'
      gap='24px'
      width='100%'
      style={variant === 'datosGenerales' ? GRID_DATOS_GENERALES : GRID_THREE_COL}
    >
      {padToGridColumns(Children.toArray(children), columnCount)}
    </Card>
  );
};

interface InfoFieldProps {
  label: string;
  value: string;
}

const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => (
  <Card display='flex' gap='4px' width='100%' style={{ minWidth: 0 }}>
    <P type='2' variant='neutral' weight='bold' withMargin={false}>
      {label.endsWith(':') ? label : `${label}:`}
    </P>
    <P type='2' variant='neutral-1' withMargin={false}>
      {value || '-'}
    </P>
  </Card>
);

const GeneralDataTab: React.FC<GeneralDataTabProps> = ({ clientData }) => {
  const { clientTheme } = useClientTheme();
  const { isUruguayEnabled, isClientCategoryTypeEnabled } = useConfig();
  const regionLabel = isUruguayEnabled ? 'Departamento' : 'Provincia';
  const { basic_data: basicData } = clientData;

  const getActivityDescription = (activityCode: string) => {
    const activity = economicActivities.find(a => String(a.code) === String(activityCode));
    return activity ? `${activityCode} - ${activity.label}` : activityCode;
  };

  const getIvaStatusDescription = (ivaCode: string) => {
    const ivaStatus = ivaStatuses.find(status => String(status.code) === String(ivaCode));
    return ivaStatus ? ivaStatus.label : ivaCode;
  };

  type PartnerRow = GeneralDataTabClientData['corporate_structure'][number];

  const partnersColumns = [
    {
      name: 'Nombre',
      selector: (row: PartnerRow) => row.first_name,
      cell: (row: PartnerRow) => <span>{row.first_name}</span>,
    },
    {
      name: isUruguayEnabled ? 'Cédula' : 'Documento',
      selector: (row: PartnerRow) => row.ci,
      cell: (row: PartnerRow) => <span>{row.ci}</span>,
    },
    {
      name: 'Cargo',
      selector: (row: PartnerRow) => row.position,
      cell: (row: PartnerRow) => <span>{row.position}</span>,
    },
    {
      name: 'Participación',
      selector: (row: PartnerRow) => row.ownership_interest,
      cell: (row: PartnerRow) => <span>{row.ownership_interest}%</span>,
    },
    ...(!isUruguayEnabled
      ? [
          {
            name: 'Bienes',
            selector: (row: PartnerRow) => row.assets,
            cell: (row: PartnerRow) => <span>{row.assets || '-'}</span>,
          },
          {
            name: 'Valor',
            selector: (row: PartnerRow) => row.value,
            cell: (row: PartnerRow) => (
              <span>{row.value ? `$${row.value.toLocaleString()}` : '-'}</span>
            ),
          },
        ]
      : []),
  ];

  const renderSectionHeader = (title: string) => (
    <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'} display='flex'>
      <H withMargin={false} type='3' weight='bold' modifier='strong'>
        {title}
      </H>
    </Card>
  );

  const dgiStatusDisplay =
    normalizeDgiStatus(basicData.dgi_status) || basicData.dgi_status || '-';

  const generalDataRowVariant = isUruguayEnabled ? 'datosGenerales' : 'threeCol';

  return (
    <Card spacing="4">
      <Card margin={{ bottom: '4' }}>
        <H withMargin={false} type='2' weight='bold' modifier='strong'>
          {basicData.company_name}
        </H>
      </Card>

      <Card
        display='flex'
        flexDirection='column'
        gap={ROW_GAP}
        margin={{ bottom: '4' }}
        width='100%'
      >
        <FieldRow variant={generalDataRowVariant}>
          <InfoField label="Personería" value={basicData.legal_entity} />
          <InfoField
            label="Fecha de constitución"
            value={formatDateOnlyLocal(basicData.date_incorporation)}
          />
          {isClientCategoryTypeEnabled && (
            <InfoField label="Rubro" value={clientData.category_type?.name ?? ''} />
          )}
        </FieldRow>

        <FieldRow variant={generalDataRowVariant}>
          <InfoField label="Domicilio fiscal" value={basicData.company_address} />
          <InfoField label="Localidad" value={basicData.city} />
          <InfoField label={regionLabel} value={basicData.state} />
          {isUruguayEnabled && (
            <InfoField label="Código Postal" value={basicData.postal_code ?? ''} />
          )}
        </FieldRow>

        <FieldRow variant={generalDataRowVariant}>
          <InfoField label="Correo electrónico" value={basicData.email} />
          <InfoField label="Teléfono" value={basicData.phone_number} />
        </FieldRow>

        {!isUruguayEnabled && (
          <FieldRow variant={generalDataRowVariant}>
            <InfoField
              label="Actividad económica"
              value={getActivityDescription(basicData.activity)}
            />
            <InfoField
              label="Condición frente al IVA"
              value={getIvaStatusDescription(basicData.iva_status)}
            />
          </FieldRow>
        )}
      </Card>

      {isUruguayEnabled && (
        <>
          <Card margin={{ top: '4' }}>
            {renderSectionHeader('Proveedor del Estado')}
            <Card margin={{ top: '3' }} width='100%'>
              <FieldRow variant='threeCol'>
                <InfoField
                  label="Vínculo contractual"
                  value={formatBooleanAsYesNo(basicData.contractual_link)}
                />
                <InfoField
                  label="Persona políticamente expuesta"
                  value={formatBooleanAsYesNo(basicData.politically_exposed)}
                />
                <InfoField
                  label="Registro Único de Proveedores del Estado (RUPE)"
                  value={formatBooleanAsYesNo(basicData.state_supplier_registry)}
                />
              </FieldRow>
            </Card>
          </Card>

          <Card margin={{ top: '4' }}>
            {renderSectionHeader('Inscripción DGI')}
            <Card margin={{ top: '3' }} width='100%'>
              <FieldRow variant='threeCol'>
                <InfoField label="Estado DGI" value={dgiStatusDisplay} />
                <InfoField
                  label="Fecha de emisión"
                  value={formatSummaryDate(basicData.dgi_issue_date)}
                />
                <InfoField
                  label="Fecha de vencimiento"
                  value={formatSummaryDate(basicData.dgi_expiration_date)}
                />
              </FieldRow>
            </Card>
          </Card>

          <Card margin={{ top: '4' }}>
            {renderSectionHeader('Certificado común BPS')}
            <Card margin={{ top: '3' }} width='100%'>
              <FieldRow variant='threeCol'>
                <InfoField
                  label="Número certificado BPS"
                  value={basicData.bps_certificate_number ?? ''}
                />
                <InfoField
                  label="Fecha de emisión"
                  value={formatSummaryDate(basicData.bps_issue_date)}
                />
                <InfoField
                  label="Fecha de vencimiento"
                  value={formatSummaryDate(basicData.bps_expiration_date)}
                />
              </FieldRow>
            </Card>
          </Card>
        </>
      )}

      <Card margin={{ top: '5' }}>
        <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'} display='flex' justifyContent='space-between'>
          <H withMargin={false} type='3' weight='bold' modifier='strong'>
            Socios
          </H>
        </Card>
        <Card
          display='flex'
          justifyContent='flex-end'
          gap='4px'
          margin={{ top: '2' }}
        >
          <Checkbox
            checked={clientData.has_valid_corporate_composition}
            disabled={true}
            id='summary_has_valid_corporate_composition-checkbox'
            size='md'
          >
            La información se ajusta a los documentos
          </Checkbox>
        </Card>

        <Card margin={{ top: '3' }}>
          <Table
            columns={partnersColumns}
            data={clientData.corporate_structure}
            tableData={{
              page: 0,
              size: clientData.corporate_structure.length,
              totalElements: clientData.corporate_structure.length,
              totalPages: 1,
            }}
            variant="weak"
          />
        </Card>
      </Card>
    </Card>
  );
};

export default GeneralDataTab;
